import { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { List, withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { ability, ActionMap } from '@oliasoft/authorization-ui';
import { IAccessControlModalsRef } from '@oliasoft/hierarchy-gui';
import { FeatureFlags } from '@oliasoft/feature-flags';

import { getItems, itemSelected, listCleaned } from 'store/entities/hierarchy/hierarchy';
import { HierarchyLevelType, IdFieldMap } from 'src/enums/hierarchy';
import { canManageHierarchySettings } from 'src/views/settings/company-settings/hierarchy/utils';
import { ISelectedProject, THierarchyEntity } from 'client/common/interfaces/hierarchy.interfaces.ts';
import { useListSorting } from 'client/common/sorting/use-list-sorting.tsx';
import { FetchingList } from 'client/common/lists/fetching-list.tsx';
import { isEmpty } from 'lodash';
import { HierarchyEntitySettingsShape } from 'src/common/interfaces/hierarchy-settings.interface';
import { selectHierarchyLabelsSettings } from 'src/store/entities/company-settings/selectors';
import { getEntityAccessRightsList } from 'src/store/entities/access-control/access-control';
import { getAccessControlMenu } from 'src/common/hooks/use-access-control-menu';
import { CaslEnums } from 'src/enums/casl-enums';
import type { TRootState } from 'src/store/store-types';
import { convertToKebabCase } from 'client/common/utils/string-utils.ts';
import { updateProjectHierarchy } from '../hierarchy-utils/local-storage-util';
import { getHierarchyLevelLabel } from '../hierarchy-utils/utils';

type HierarchyEntitiesListProps = PropsFromRedux & {
  companyId: string;
  entities: THierarchyEntity[];
  activeParentEntity: THierarchyEntity | null;
  isEntityFetching: boolean;
  addButtonDisabled?: boolean;
  createItem: () => void;
  onClickDelete: (id: string, type: HierarchyLevelType) => void;
  editItem: (item: THierarchyEntity, type: HierarchyLevelType) => void;
  selectedProject: ISelectedProject;
  settings: HierarchyEntitySettingsShape;
  hierarchyLabelType: string;
  entityType: HierarchyLevelType;
  parentEntityType?: HierarchyLevelType;
  childEntityType: HierarchyLevelType;
  accessControlChildRef: React.RefObject<IAccessControlModalsRef | null>;
};

const HierarchyEntitiesList = ({
  companyId,
  entities,
  onClickDelete,
  activeParentEntity,
  getItems,
  itemSelected,
  listCleaned,
  addButtonDisabled,
  isEntityFetching,
  createItem,
  editItem,
  selectedProject,
  settings,
  hierarchyLabelType,
  users,
  apiKeys,
  entityAccessRightsList,
  entityAccessRightsMetadata,
  getEntityAccessRightsList,
  entityType,
  parentEntityType,
  childEntityType,
  accessControlChildRef,
}: HierarchyEntitiesListProps) => {
  const { t } = useTranslation();

  const selectedEntityId =
    entityType === HierarchyLevelType.Country ? selectedProject.countryId : selectedProject[`${entityType}Id`];

  const parentId =
    parentEntityType && activeParentEntity
      ? (activeParentEntity as any)?.[IdFieldMap.get(parentEntityType)!]
      : undefined;

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      const isCountryEntity = entityType === HierarchyLevelType.Country;
      const shouldFetch = isCountryEntity || !!parentId;
      if (!shouldFetch) {
        return;
      }
      if (isCountryEntity) {
        await getItems(companyId, HierarchyLevelType.Country);
      } else {
        await getItems(companyId, entityType, parentId!);
      }
      if (!cancelled && !isEmpty(selectedEntityId)) {
        itemSelected({
          id: selectedEntityId,
          type: entityType,
          clearChildren: false,
        });
      }
    };

    fetchData();

    return () => {
      cancelled = true;
      listCleaned(entityType);
    };
  }, [companyId, entityType, parentId, selectedEntityId]);

  useEffect(() => {
    accessControlChildRef.current?.accessRightListUpdated({
      accessRightsList: entityAccessRightsList,
      metadata: [entityAccessRightsMetadata],
    });
  }, [entityAccessRightsList]);

  const { items, sortAction } = useListSorting(entities);

  if (isEntityFetching) {
    return <FetchingList testId={`${entityType}-list`} name={getHierarchyLevelLabel(entityType, hierarchyLabelType)} />;
  }

  return (
    <>
      <List
        testId={`${entityType}-list`}
        list={{
          name: getHierarchyLevelLabel(entityType, hierarchyLabelType),
          actions: [
            sortAction,
            {
              label: 'Add',
              icon: 'add',
              onClick: () => createItem(),
              disabled:
                addButtonDisabled || isEntityFetching || (settings?.lockNodeCreation && !canManageHierarchySettings()),
            },
          ],
          items: items.map((item) => {
            const itemid = item[IdFieldMap.get(entityType)!];
            const { childrenCount } = item.metadata;
            const canManageAccess = ability.can(ActionMap.MANAGE, CaslEnums.Access);
            const accessControlIsActive = FeatureFlags.isActive('Access control in Hierarchy');

            return {
              testId: `${entityType}-list-${convertToKebabCase(item.name)}-item`,
              id: itemid,
              name: item.name,
              metadata: `${childrenCount} ${getHierarchyLevelLabel(
                childEntityType,
                hierarchyLabelType,
                +childrenCount !== 1,
              )}`,
              active: item.active,
              onClick: () => {
                itemSelected({
                  id: itemid,
                  type: entityType,
                });
                updateProjectHierarchy(`${entityType}Id`, itemid, selectedProject);
              },
              actions: [
                {
                  label: 'Update',
                  icon: 'edit',
                  onClick: () => editItem(item, entityType),
                },
                {
                  label: 'Delete',
                  icon: 'delete',
                  onClick: () => onClickDelete(itemid, entityType),
                  disabled: settings?.lockNodeDeletion && !canManageHierarchySettings(),
                },
                canManageAccess && accessControlIsActive
                  ? {
                      childComponent: getAccessControlMenu({
                        t,
                        accessControlChildRef,
                        item: { ...item, id: itemid, entityType },
                        users,
                        apiKeys,
                        getEntityAccessRightsList,
                      }),
                    }
                  : {},
              ],
            };
          }),
        }}
        stickyHeader
        expanding
        scrollDetails={{
          scrollable: true,
          hideScrollbar: true,
        }}
      />
    </>
  );
};

const mapStateToProps = ({ entities }: TRootState) => ({
  hierarchyLabelType: selectHierarchyLabelsSettings(entities),
  apiKeys: entities?.accessControl.apiKeys,
  users: entities?.user.users,
  entityAccessRightsList: entities?.accessControl.accessRights,
  entityAccessRightsMetadata: entities?.accessControl.metadata,
});

const mapDispatchToProps = {
  getItems,
  itemSelected,
  listCleaned,
  getEntityAccessRightsList,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = withErrorBoundary(connector(HierarchyEntitiesList));

export { Container as HierarchyEntitiesList };
